import React, { useState, useEffect, useRef } from "react";
import PublicLocationIcon from "../../../assets/images/icon/public-location-marker.svg";
import PrivateLocationIcon from "../../../assets/images/icon/private-location-marker.svg";
import {
  Directions,
  GoogleMapConstants,
  sportConstants,
  spotConstants,
  translationConstants,
} from "../../../constants";
import { useDispatch, useSelector } from "react-redux";
import { googleMapActions } from "../../../redux/actions/googleMap.action";
import { useTranslation } from "react-i18next";
import {
  Marker,
  GoogleMap,
  InfoWindowF,
  MarkerClusterer,
} from "@react-google-maps/api";
import { findConstantValue } from "../../../system";

const {
  LOCATION_MARKER_ICON_WIDTH,
  LOCATION_MARKER_ICON_HEIGHT,
  DEFAULT_LATITUDE,
  DEFAULT_LONGITUDE,
} = GoogleMapConstants;

const GoogleMapView = (props) => {
  const { t } = useTranslation();
  const { zoom = GoogleMapConstants.DEFAULT_ZOOM } = props;
  const { isShowMark = true } = props.locations[0] || {};
  const dispatch = useDispatch();
  const mapRef = useRef(null);
  const direction = useSelector(
    (state) => state.LanguageChangeReducer.direction
  );
  const [center, setCenter] = useState({
    lat: DEFAULT_LATITUDE,
    lng: DEFAULT_LONGITUDE,
  });
  const [currentLocation, setCurrentLocation] = useState(null);
  const [selected, setSelected] = useState(null);
  const [service, setService] = useState(null);

  const createKey = (location, index) =>
    `${location.lat}${location.lng}${index}`;

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          let location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          dispatch(
            googleMapActions.getLocationDetail(
              position.coords.latitude,
              position.coords.longitude
            )
          );
          setCurrentLocation(location);
        },
        (error) => {}
      );
    }
    // eslint-disable-next-line
  }, []);

  const calculateBoundingBox = (locations) => {
    if (!locations || locations.length === 0) {
      return null; // Return null if no locations are provided
    }

    // Initialize variables for min and max latitude and longitude
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;
    // Find the minimum and maximum latitude and longitude
    locations.forEach((location) => {
      minLat = Math.min(minLat, location.latitude);
      maxLat = Math.max(maxLat, location.latitude);
      minLng = Math.min(minLng, location.longitude);
      maxLng = Math.max(maxLng, location.longitude);
    });

    // Return the bounding box
    return {
      north: maxLat,
      south: minLat,
      east: maxLng,
      west: minLng,
    };
  };

  useEffect(() => {
    if (mapRef.current && props.locations?.length) {
      const boundingBox = calculateBoundingBox(props.locations);

      if (boundingBox) {
        const bounds = new window.google.maps.LatLngBounds(
          { lat: boundingBox.south, lng: boundingBox.west },
          { lat: boundingBox.north, lng: boundingBox.east }
        );
        mapRef.current.fitBounds(bounds);

        window.google.maps.event.addListenerOnce(mapRef.current, "idle", () => {
          if (mapRef.current.getZoom() > GoogleMapConstants.MAX_ZOOM) {
            mapRef.current.setZoom(GoogleMapConstants.MAX_ZOOM);
          }
        });
      }
    }
  }, [props.locations]);

  const onLoad = (map) => {
    if (props.locations && props.locations.length > 0) {
      const boundingBox = calculateBoundingBox(props.locations);
      const bounds = new window.google.maps.LatLngBounds(
        { lat: boundingBox.south, lng: boundingBox.west },
        { lat: boundingBox.north, lng: boundingBox.east }
      );
      map.fitBounds(bounds);
    }
    mapRef.current = map;
  };

  const handleSelectedLocation = (location) => {
    let sportsList = [];
    location?.sports?.forEach((sport, index) => {
      sportsList.push(t(findConstantValue(sport, sportConstants)));
    });
    const locationObj = {
      locationName:
        direction === Directions.rtl
          ? location?.locationArabicName || location?.locationName
          : location?.locationName,
      centerName:
        direction === Directions.rtl
          ? location?.centerArabicName || location?.centerName
          : location?.centerName,
      spotType: t(
        findConstantValue(location?.spotTypeId, spotConstants.spotType)
      ),
      sports: sportsList,
      longitude: location.longitude,
      latitude: location.latitude,
      isPublic: location.isPublic,
    };
    setSelected(locationObj);
  };
  return (
    <div>
      <GoogleMap
        zoom={zoom}
        id="googleMap"
        center={center}
        onLoad={onLoad}
        options={props.options}
        mapContainerStyle={props.containerStyle}
      >
        {props.locations?.length > 0 && (
          <>
            {isShowMark && (
              <MarkerClusterer options={props.locations} gridSize={2}>
                {(cluster) =>
                  props.locations.map((location, index) => (
                    <Marker
                      position={{
                        lat: location.latitude,
                        lng: location.longitude,
                      }}
                      clusterer={cluster}
                      key={createKey(location, index)}
                      icon={{
                        url: location.isPublic
                          ? PrivateLocationIcon
                          : PublicLocationIcon,
                        scaledSize: new window.google.maps.Size(
                          LOCATION_MARKER_ICON_WIDTH,
                          LOCATION_MARKER_ICON_HEIGHT
                        ),
                        origin: new window.google.maps.Point(0, 0),
                        anchor: new window.google.maps.Point(
                          LOCATION_MARKER_ICON_WIDTH / 2,
                          LOCATION_MARKER_ICON_HEIGHT / 2
                        ),
                      }}
                      onClick={() => {
                        handleSelectedLocation(location);
                      }}
                    />
                  ))
                }
              </MarkerClusterer>
            )}

            {selected && selected?.locationName ? (
              <InfoWindowF
                onCloseClick={() => {
                  setSelected(null);
                }}
                position={{ lat: selected.latitude, lng: selected.longitude }}
              >
                <>
                  <div className="row">
                    <div className="col-12">
                      <u>
                        <b className="map-tool-tip-heading">
                          {t(translationConstants.LOCATION_NAME)}
                        </b>
                      </u>
                    </div>
                    <div className="col-12 map-tool-tip-text pt-2">
                      {selected?.locationName}
                    </div>
                  </div>
                  {selected?.centerName && (
                    <>
                      <div className="row mt-2">
                        <div className="col-12">
                          <u>
                            <b className="map-tool-tip-heading">
                              {t(translationConstants.CENTER_NAME)}
                            </b>
                          </u>
                        </div>
                        <div className="col-12 map-tool-tip-text pt-2">
                          {selected?.centerName}
                        </div>
                      </div>
                    </>
                  )}
                  {selected?.spotType && (
                    <>
                      <div className="row mt-2">
                        <div className="col-12">
                          <u>
                            <b className="map-tool-tip-heading">
                              {t(translationConstants.LOCATION_TYPE)}
                            </b>
                          </u>
                        </div>
                        <div className="col-12 map-tool-tip-text pt-2">
                          {selected?.isPublic
                            ? t(translationConstants.PRIVATE)
                            : t(translationConstants.PUBLIC)}
                        </div>
                      </div>
                    </>
                  )}
                  {selected?.sports && (
                    <>
                      <div className="row mt-2">
                        <div className="col-12">
                          <u>
                            <b className="map-tool-tip-heading">
                              {t(translationConstants.SPORTS)}
                            </b>
                          </u>
                        </div>
                        <div className="col-12 mt-2">
                          {selected?.sports?.map((sport, i) => (
                            <ol className="list-circle mb-1" key={i}>
                              <li className="map-tool-tip-text">{sport}</li>
                            </ol>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </>
              </InfoWindowF>
            ) : null}
          </>
        )}
      </GoogleMap>
    </div>
  );
};

export default GoogleMapView;
